import React, { useState } from "react";
import { LinkedCasesView } from "../types";
import { formatDate } from "../lib/format";

// How many cases render before "Show all". A timeshare resort can carry
// hundreds; the server already caps the list and sends the exact total.
const COLLAPSED_COUNT = 8;

interface Props {
  linkedCases?: LinkedCasesView;
  isTimeshare?: boolean;
}

/**
 * "Court cases at this property" — shown when two or more foreclosure cases
 * are attached to one property. The dominant case is a timeshare resort,
 * where each case forecloses one owner's interval (unit + week), not the
 * whole building: one lead, many cases. Renders nothing otherwise.
 */
export default function LinkedCasesSection({ linkedCases, isTimeshare }: Props) {
  const [showAll, setShowAll] = useState(false);
  if (!linkedCases || linkedCases.total < 2 || linkedCases.cases.length === 0) return null;

  const timeshare = isTimeshare || linkedCases.isTimeshare;
  const cases = linkedCases.cases;
  const shown = showAll ? cases : cases.slice(0, COLLAPSED_COUNT);
  const hasInterval = timeshare && cases.some((c) => c.unit || c.week);
  const hasOwner = cases.some((c) => c.ownerName);
  const notListed = linkedCases.total - cases.length;

  return (
    <div className="detail-group" data-testid="linked-cases">
      <label>Court cases at this property ({linkedCases.total})</label>
      <p className="linked-cases-note">
        {timeshare
          ? "This is a timeshare resort. Each case forecloses one owner's interval (unit and week), not the whole property — bidding on one case buys that interval only."
          : "More than one foreclosure case is filed against this property."}
      </p>
      <table className="party-table linked-cases-table">
        <tbody>
          {shown.map((c) => (
            <tr key={c.courtFileNumber}>
              <td className="linked-case-number">{c.courtFileNumber}</td>
              {hasInterval && (
                <td className="linked-case-interval">
                  {[c.unit && `Unit ${c.unit}`, c.week && `Week ${c.week}`].filter(Boolean).join(" · ") || "—"}
                </td>
              )}
              {hasOwner && <td className="party-name">{c.ownerName || "—"}</td>}
              <td className="party-role">{c.saleDate ? `Sale ${formatDate(c.saleDate)}` : "No sale date"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {cases.length > COLLAPSED_COUNT && (
        <button type="button" className="link-button" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Show fewer" : `Show all ${cases.length} listed cases`}
        </button>
      )}
      {notListed > 0 && (showAll || cases.length <= COLLAPSED_COUNT) && (
        <p className="linked-cases-note">+{notListed} older cases not listed.</p>
      )}
    </div>
  );
}
